import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { ArrowUp, Square, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import { Textarea } from "../Textarea/Textarea"

export type ChatInputPrimaryAction = "send" | "stop" | "cancel"

/**
 * The "something needs doing" surface: warning border over an OPAQUE warning
 * tint (the dim token is translucent, so it is layered on the background).
 * Opaque so a tab attached above the card can cover the card's top border and
 * read as one piece with it — both use this exact class.
 */
export const CHAT_INPUT_WARNING_SURFACE =
  "border-warning bg-background [background-image:linear-gradient(var(--color-warning-dim),var(--color-warning-dim))]"

export interface ChatInputHandle {
  focus: () => void
}

export interface ChatInputProps {
  id?: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  /**
   * The box clears on submit. A promise resolving `false` means nothing was sent:
   * the text comes back, unless the user has started typing something else.
   */
  onSubmit?: (value: string) => void | Promise<boolean | void>
  onStop?: () => void
  onCancel?: () => void
  primaryAction?: ChatInputPrimaryAction
  placeholder?: string
  disabled?: boolean
  isMobileLayout?: boolean
  bannerSlot?: ReactNode
  editingSlot?: ReactNode
  /**
   * Inside the card, above the textarea (the chat's "Chart updated" panel:
   * what changed, with the box below for an optional message).
   */
  headerSlot?: ReactNode
  /**
   * A labeled send button in place of the arrow (e.g. "Send to AI"). It still
   * submits the box, so Enter and the button do the same thing. Gives way to
   * Stop / Cancel like the arrow does.
   */
  sendLabel?: ReactNode
  /** The box may be sent empty — what goes out is more than the text (the workbench's Send to AI). */
  allowEmptySubmit?: boolean
  /** Longer text cannot be sent. Also caps typing, but not text already in the box. */
  maxLength?: number
  /** `warning` paints the card with CHAT_INPUT_WARNING_SURFACE. */
  tone?: "default" | "warning"
  /** Extra classes for the bordered card itself (e.g. a corner squared under a tab). */
  cardClassName?: string
  /** Chrome-row controls that sit left of the send button (e.g. the effort selector). */
  leadingChromeSlot?: ReactNode
  /** Far-left chrome-row control (the Auto-verify switch). An empty spacer when absent. */
  chromeStartSlot?: ReactNode
  overlaySlot?: ReactNode
  trailingChromeSlot?: ReactNode
  className?: string
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      id,
      value: controlled,
      defaultValue = "",
      onChange,
      onSubmit,
      onStop,
      onCancel,
      primaryAction = "send",
      placeholder = "Ask anything…",
      disabled = false,
      isMobileLayout = false,
      bannerSlot,
      editingSlot,
      headerSlot,
      sendLabel,
      allowEmptySubmit = false,
      maxLength,
      tone = "default",
      cardClassName,
      leadingChromeSlot,
      chromeStartSlot,
      overlaySlot,
      trailingChromeSlot,
      className,
    },
    ref,
  ) => {
    const [internal, setInternal] = useState(defaultValue)
    const value = controlled ?? internal
    const setValue = useCallback(
      (next: string) => {
        if (controlled === undefined) setInternal(next)
        onChange?.(next)
      },
      [controlled, onChange],
    )

    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value),
      [setValue],
    )

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }))

    useEffect(() => {
      if (!isMobileLayout) return
      const t = textareaRef.current
      if (!t) return
      t.style.height = "auto"
      t.style.height = Math.min(t.scrollHeight, 128) + "px"
    }, [value, isMobileLayout])

    // `maxLength` on the textarea stops typing past it, but not a draft that was
    // already longer when the limit arrived (the Send to AI tab appearing).
    const tooLong = maxLength != null && value.trim().length > maxLength

    const canSubmit =
      (allowEmptySubmit || value.trim().length > 0) && !tooLong && !disabled && primaryAction === "send"

    // Read by a failed send's restore: only an untouched (still empty) box gets the text back.
    const valueRef = useRef(value)
    useEffect(() => {
      valueRef.current = value
    }, [value])

    // Back in the box after a send, until the user clicks or tabs elsewhere.
    const rootRef = useRef<HTMLDivElement | null>(null)
    const keepFocusRef = useRef(false)
    const [sentCount, setSentCount] = useState(0)
    useEffect(() => {
      if (disabled || isMobileLayout || !keepFocusRef.current) return
      textareaRef.current?.focus()
    }, [disabled, isMobileLayout, sentCount])
    useEffect(() => {
      const release = (e: Event) => {
        if (!rootRef.current?.contains(e.target as Node)) keepFocusRef.current = false
      }
      document.addEventListener("pointerdown", release, true)
      document.addEventListener("focusin", release, true)
      return () => {
        document.removeEventListener("pointerdown", release, true)
        document.removeEventListener("focusin", release, true)
      }
    }, [])

    const submit = useCallback(() => {
      const text = value.trim()
      if ((!text && !allowEmptySubmit) || tooLong || primaryAction !== "send") return
      const result = onSubmit?.(text)
      setValue("")
      keepFocusRef.current = true
      setSentCount((n) => n + 1)
      if (text && result && typeof result.then === "function") {
        void result.then(
          (sent) => {
            if (sent === false && valueRef.current === "") setValue(text)
          },
          () => {
            if (valueRef.current === "") setValue(text)
          },
        )
      }
    }, [value, allowEmptySubmit, tooLong, primaryAction, onSubmit, setValue])

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault()
          submit()
        } else if (e.key === "Escape") {
          if (primaryAction === "stop") {
            e.preventDefault()
            onStop?.()
          }
        }
      },
      [submit, primaryAction, onStop],
    )

    const actionButton = (() => {
      if (primaryAction === "cancel") {
        return (
          <Button
            type="button"
            size="icon-sm"
            variant="destructive"
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X />
          </Button>
        )
      }
      if (primaryAction === "stop") {
        return (
          <Button
            type="button"
            size="icon-sm"
            variant="secondary"
            onClick={onStop}
            aria-label="Stop"
          >
            <Square className="fill-current" />
          </Button>
        )
      }
      if (sendLabel) {
        return (
          <Button
            type="button"
            size="sm"
            className="text-label"
            disabled={!canSubmit}
            onClick={submit}
          >
            {sendLabel}
          </Button>
        )
      }
      return (
        <Button
          type="button"
          size="icon-sm"
          disabled={!canSubmit}
          onClick={submit}
          aria-label="Send message"
        >
          <ArrowUp />
        </Button>
      )
    })()

    if (overlaySlot) {
      return (
        <div id={id} className={cn("shrink-0 border-t border-border p-2 bg-background", className)}>
          {overlaySlot}
        </div>
      )
    }

    return (
      <div ref={rootRef} id={id} className={cn("shrink-0 border-t border-border p-2 bg-background", className)}>
        {bannerSlot}
        <div
          className={cn(
            "relative flex flex-col rounded-lg border overflow-hidden transition-colors",
            tone === "warning"
              ? CHAT_INPUT_WARNING_SURFACE
              : "border-border bg-card focus-within:border-ring/60",
            cardClassName,
          )}
        >
          {editingSlot}
          {/* The dividers and the chrome row keep the plain composer's look in
              either tone: only the card's own border and surface turn orange. */}
          {headerSlot && <div className="px-3 pt-2.5 pb-2 border-b border-border/40">{headerSlot}</div>}
          {isMobileLayout ? (
            <div className="flex items-end gap-1 pl-3 pr-2 pt-2 pb-1.5">
              <Textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                className={cn(
                  "flex-1 min-h-9 max-h-32 resize-none py-1.5 px-2 text-body leading-5",
                  "border-0 bg-transparent dark:bg-transparent shadow-none",
                  "focus-visible:ring-0 focus-visible:outline-none",
                  disabled && "opacity-60 cursor-not-allowed",
                )}
              />
              {actionButton}
            </div>
          ) : (
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              className={cn(
                // Under a header the box is the lesser part of the card: a bit shorter.
                headerSlot ? "min-h-14" : "min-h-20",
                "max-h-32 resize-none pt-2.5 pb-2 px-3 text-body leading-5",
                "border-0 bg-transparent dark:bg-transparent shadow-none",
                "focus-visible:ring-0 focus-visible:outline-none",
                disabled && "opacity-60 cursor-not-allowed",
              )}
            />
          )}
          {tooLong && maxLength != null && (
            <p role="alert" className="px-3 pb-1.5 text-caption text-danger">
              Too long to send: {value.trim().length.toLocaleString()} / {maxLength.toLocaleString()} characters
            </p>
          )}
          <div className="flex items-center justify-between gap-2 shrink-0 pl-3 pr-2 py-1 border-t border-border/40 bg-muted/30">
            {chromeStartSlot ?? <div />}
            <div className="flex items-center gap-1 shrink-0">
              {leadingChromeSlot}
              {isMobileLayout ? trailingChromeSlot ?? null : actionButton}
            </div>
          </div>
        </div>
      </div>
    )
  },
)
ChatInput.displayName = "ChatInput"
